import { type ReactNode } from "react";
import { useTimeRange } from "../state/time-range.tsx";

/**
 * LivePauseControl — the per-screen live/pause toggle, styled as a "liquid glass" media control.
 *
 * The live/pause state is app-wide (`useTimeRange`): pausing freezes every chart on every screen
 * (FR-13 AC2). Previously the ONLY toggle lived in the top bar; this component lets each screen
 * carry its own toggle inline in its "Live · window …" meta row, which reads as per-page while still
 * driving the one shared state. Dropped onto any screen with no props — it reads and toggles the
 * shared context directly.
 *
 * It is a single two-state segmented control: a PLAY segment (resume live) and a PAUSE segment
 * (freeze). The active segment is pressed (`aria-pressed`) and the state is conveyed by the icon +
 * an accessible label, never colour alone (design 12.4). The "liquid glass" look (translucent,
 * blurred, soft highlight) comes entirely from theme tokens so it reads correctly in light and dark.
 */
export function LivePauseControl(): ReactNode {
  const { live, toggleLive } = useTimeRange();

  return (
    <span className="live-glass" role="group" aria-label="Live updates">
      <button
        type="button"
        className="live-glass__btn"
        data-active={live ? "true" : undefined}
        aria-pressed={live}
        aria-label="Live: resume tracking now"
        title="Live"
        onClick={() => {
          if (!live) toggleLive();
        }}
      >
        {/* Play glyph (resume). */}
        <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
          <path d="M8 5.5v13a1 1 0 0 0 1.54.84l10-6.5a1 1 0 0 0 0-1.68l-10-6.5A1 1 0 0 0 8 5.5z" />
        </svg>
      </button>
      <button
        type="button"
        className="live-glass__btn"
        data-active={!live ? "true" : undefined}
        aria-pressed={!live}
        aria-label="Pause: freeze the window"
        title="Pause"
        onClick={() => {
          if (live) toggleLive();
        }}
      >
        {/* Pause glyph (freeze). */}
        <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
          <rect x="6.5" y="5" width="4" height="14" rx="1.4" />
          <rect x="13.5" y="5" width="4" height="14" rx="1.4" />
        </svg>
      </button>
    </span>
  );
}
